import type { InitDataQuery } from '@api/dto/gql-helpers.generated';
import { InitDataDocument } from '@api/dto/gql-helpers.generated';
import type { ApolloQueryResult } from '@apollo/client';
import type { EventCallable } from 'effector';
import { useUnit } from 'effector-react';
import type { PropsWithChildren } from 'react';
import { Suspense, createContext, useEffect, useMemo, useState } from 'react';

import { ThemeProvider as MuiThemeProvider } from '@mui/material';

import { STORAGE_KEYS } from '@constants/app';

import { apolloClient } from '@system/apolloClient';

import { $themeStore, initData, setData } from './store';
import type { ThemeName, ThemeValue } from './types';

export type ContextValue = {
  currentTheme: ThemeValue | null;
  changeTheme: EventCallable<ThemeName>;
};

const defaultValue: ContextValue = {
  currentTheme: null,
  changeTheme: setData,
};

export const ThemeContext = createContext<ContextValue>(defaultValue);

export function ThemeProvider(props: PropsWithChildren) {
  const [hasUser, setHasUser] = useState<boolean | null>(null);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEYS.REFRESH_GRAPHQL_TOKEN)) {
      initData(true);
    } else {
      const observable = apolloClient.watchQuery<InitDataQuery>({
        query: InitDataDocument,
        fetchPolicy: 'cache-only',
      });

      const apolloSubscription = observable.subscribe({
        next: (result: ApolloQueryResult<InitDataQuery>) => {
          const user = result.data?.users.me;
          setHasUser(!!user);
        },
      });

      initData(!!hasUser);

      return () => {
        apolloSubscription.unsubscribe();
      };
    }
  }, [hasUser]);

  const { loading, theme } = useUnit($themeStore);

  const value: ContextValue = useMemo(
    () => ({
      changeTheme: setData,
      currentTheme: theme,
    }),
    [theme],
  );

  if (loading || !theme) {
    return <></>;
  }

  return (
    <Suspense>
      <ThemeContext.Provider value={value}>
        <MuiThemeProvider theme={theme.theme}>
          {props.children}
        </MuiThemeProvider>
      </ThemeContext.Provider>
    </Suspense>
  );
}
